import { WorldState, groupByPos, applyOnMatch } from "./world";
import {
  CharacterState,
  StatusType,
  CharacterSex,
  hasStatus,
  addStatus,
  CharacterHandler as CH,
  stripStatus,
  CharacterOptions,
  PregnantStatus,
  hasStatusFilter,
} from "./characters";
import { StatusHandler as SH } from "./status";
import config from "../worldConfig";
import { crossGenes, Gene, geneSelection, GeneTrait } from "./genetics";
import { partition } from "../utils/dictionaries";
import { FactionType, SocialHandler } from "./social";

const {
  HAVING_SEX_BASE_CHANCE,
  PREGNANCY_BASE_CHANCE,
  CHARACTER_AGE_ADULT_START,
  PREGNANCY_AGE_MAX,
  PREGNANCY_DURATION,
  BIRTH_DEATH_CHANCE,
} = config;

type HadSexWith = {
  with: string;
  withGenes: Gene[];
  willBePregnant?: boolean;
};

const rollForPregnancy = (c: CharacterState): boolean => {
  const roll =
    Math.random() *
    (c.genes.find((g) => g.trait === GeneTrait.FERTILE) ? 0.8 : 1);
  return (
    c.sex === CharacterSex.FEMALE &&
    !hasStatus(StatusType.PREGNANT, c) &&
    roll < PREGNANCY_BASE_CHANCE
  );
};

export const doBirth = (w: WorldState): WorldState => {
  const birthReady = w.characters.filter((c) =>
    c.statusList.find(
      (s) => s.type === StatusType.PREGNANT && s.duration > PREGNANCY_DURATION
    )
  );
  const children = birthReady.map((mother) => {
    const pregnantStatus: PregnantStatus = mother.statusList.find(
      (s) => s.type === StatusType.PREGNANT
    ) as PregnantStatus;

    const newGenes = crossGenes(
      mother.genesLatent,
      pregnantStatus.payload.withGenes
    );

    /*if (
      newGenes.length > 1 &&
      newGenes.filter((g) => g.weight > 0.75).length > 0
    ) {
      console.log("newGenes", newGenes);
    }*/

    const childOptions: CharacterOptions = {
      pos: mother.pos,
      lastname: mother.lastname,
      parents: {
        mother: mother.id,
        father: pregnantStatus.payload.with,
      },
      genesLatent: newGenes,
      genes: geneSelection(newGenes),
      factions: mother.factions.filter(
        (f) => f.factionType === FactionType.FAMILY
      ), //@TODO: could also be father one instead depending on social rules
    };

    return CH.create(w, childOptions);
  });

  const birthEffect = (c: CharacterState): CharacterState => {
    const isBirthReady: boolean =
      c.statusList.find(
        (s) => s.type === StatusType.PREGNANT && s.duration > PREGNANCY_DURATION
      ) != null;

    if (!isBirthReady) {
      return c;
    }

    const birthKillEffect = (char: CharacterState) =>
      Math.random() < BIRTH_DEATH_CHANCE
        ? addStatus(
            SH.createDmgStatus(StatusType.PREGNANT, { dmg: 1000 }),
            char
          )
        : char;
    const removePregnancyEffect = (char: CharacterState) =>
      stripStatus(StatusType.PREGNANT)(char);

    return [removePregnancyEffect, birthKillEffect].reduce(
      (newChar, effect) => effect(newChar),
      c
    );
  };

  const newCharacters: CharacterState[] = [
    ...w.characters.map(birthEffect), //@TODO: SIMPLIFY WHEN WE CAN CHECK WETHER children has parent or not
    ...children,
  ];

  return {
    ...w,
    characters: newCharacters,
  };
};

type SexParition = {
  fertileMales: CharacterState[];
  fertileFemales: CharacterState[];
  others: CharacterState[];
};

const getHaveSexPartitions = (chars: CharacterState[]): SexParition => {
  const [eligibleForSex, notEligibleForSex] = partition(chars, CH.canHaveSex);

  const [fertileMales, fertileFemales] = partition(
    eligibleForSex,
    (c) => c.sex === CharacterSex.MALE
  );

  return {
    fertileMales,
    fertileFemales,
    others: notEligibleForSex,
  };
};

const doPregnancy2 = (w: WorldState): WorldState => {
  const { fertileMales, fertileFemales, others } = getHaveSexPartitions(
    w.characters
  );

  const mapSexToPregnancy = (c: CharacterState): CharacterState => {
    const sexStatus = c.statusList.find((s) => s.type === StatusType.HAD_SEX);
    if (!sexStatus || !sexStatus.payload.willBePregnant) {
      return c;
    }

    return addStatus(
      SH.createStatus(StatusType.PREGNANT, {
        with: sexStatus.payload.with,
        withGenes: sexStatus.payload.withGenes,
      }),
      c
    );
  };
  const newFertileFemales: CharacterState[] =
    fertileFemales.map(mapSexToPregnancy);

  return {
    ...w,
    characters: [...fertileMales, ...newFertileFemales, ...others],
  };
};

export const doSex2 = (w: WorldState): WorldState => {
  const { agentGroups: charactersPerPos, meta: _ } =
    SocialHandler.SplitByFaction(FactionType.POS, w.characters, w.factions);

  const getHadSexResult = (sp: SexParition): CharacterState[] => {
    const { fertileMales, fertileFemales, others } = sp;

    const havingSexRolls = Array.from(
      Array(Math.max(fertileFemales.length, fertileMales.length))
    ).map((e) => Math.random());

    const newMales: CharacterState[] = fertileMales.map((c, i) => {
      if (
        i >= fertileFemales.length ||
        havingSexRolls[i] > HAVING_SEX_BASE_CHANCE
      ) {
        return c;
      }

      return addStatus(
        SH.createStatus(StatusType.HAD_SEX, {
          with: fertileFemales[i].id,
          weak: true,
        }),
        c
      );
    });

    const newFemales: CharacterState[] = fertileFemales.map((c, i) => {
      if (
        i >= fertileMales.length ||
        havingSexRolls[i] > HAVING_SEX_BASE_CHANCE
      ) {
        return c;
      }

      return addStatus(
        SH.createStatus(StatusType.HAD_SEX, {
          with: fertileMales[i].id,
          withGenes: fertileMales[i].genesLatent,
          willBePregnant: rollForPregnancy(c),
          weak: true,
        }),
        c
      );
    });

    return [...newMales, ...newFemales, ...others];
  };

  const newCharacters = charactersPerPos.flatMap((chars) => {
    const haveSexPart = getHaveSexPartitions(chars);
    const sexRes = getHadSexResult(haveSexPart);
    return sexRes;
  });

  return doPregnancy2({
    ...w,
    characters: newCharacters,
  });
};

export const doSex = (w: WorldState): WorldState => {
  const groupedCharacters = w.characters.reduce(
    groupByPos<CharacterState>(),
    {}
  );

  //@TODO: prendre en compte une notion d'attirance/opinion
  const rollForSex = (m: CharacterState, f: CharacterState): boolean => {
    return Math.random() < HAVING_SEX_BASE_CHANCE;
  };

  //@TODO: faire un merge entre les couples déjà connus et ceux restants (et plus tard, prendre en compte l'infidelité)
  const hadSex: { [id: string]: HadSexWith } = Object.entries(groupedCharacters)
    .map(([key, chars]) => {
      const eligibleForSex = chars.filter((c: CharacterState) => {
        return (
          CH.age(c) >= CHARACTER_AGE_ADULT_START &&
          (c.sex === CharacterSex.MALE || CH.age(c) < PREGNANCY_AGE_MAX)
        );
      });
      const males: CharacterState[] = eligibleForSex.filter(
        (c: CharacterState) => c.sex === CharacterSex.MALE
      );
      const females: CharacterState[] = eligibleForSex.filter(
        (c: CharacterState) => c.sex === CharacterSex.FEMALE
      );

      const sexCouples = males.map((c: CharacterState, i: number) => {
        if (females.length <= i) {
          return null;
        }

        if (!rollForSex(c, females[i])) {
          return null;
        }

        const sexBetween = {
          [c.id]: { with: females[i].id, withGenes: females[i].genes },
          [females[i].id]: {
            with: c.id,
            withGenes: c.genes,
            willBePregnant: rollForPregnancy(females[i]),
          },
        };

        return sexBetween;
      });

      return (
        sexCouples.reduce(
          (acc, couple) => (couple != null ? { ...acc, ...couple } : acc),
          {}
        ) || {}
      );
    })
    .reduce((acc, group) => ({ ...acc, ...group }), {});

  const resolvePregnancy =
    (hs: { [id: string]: HadSexWith }) =>
    (c: CharacterState): CharacterState => {
      if (!hs[c.id].willBePregnant) {
        return c;
      }

      return addStatus(
        {
          type: StatusType.PREGNANT,
          duration: 0,
          payload: {
            with: hs[c.id].with,
            withGenes: hs[c.id].withGenes,
          },
        },
        c
      );
    };

  const shouldBePregnant = (e: CharacterState): boolean => {
    return hadSex[e.id] !== undefined && hadSex[e.id].willBePregnant === true;
  };

  return {
    ...w,
    characters: applyOnMatch(
      w.characters,
      shouldBePregnant,
      resolvePregnancy(hadSex)
    ),
  };
};
